import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import PostCard from '../../components/PostCard';

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'ahora';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

export default function MemberPostDetail() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, c] = await Promise.all([
          api.get(`/community/posts/${postId}`),
          api.get(`/community/posts/${postId}/comments`),
        ]);
        if (cancelled) return;
        setPost(p.data);
        setComments(c.data);
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || 'Publicación no encontrada');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [postId]);

  const handleLike = async () => {
    try {
      const { data } = await api.post(`/community/posts/${postId}/like`);
      setPost((prev) => ({ ...prev, liked_by_me: data.liked, like_count: data.like_count }));
    } catch {
      toast.error('No se pudo registrar tu reacción.');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/community/posts/${postId}/comments`, { text: t });
      setComments((prev) => [...prev, data]);
      setPost((prev) => ({ ...prev, comment_count: (prev.comment_count || 0) + 1 }));
      setText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo comentar.');
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar esta publicación?')) return;
    try {
      await api.delete(`/community/posts/${postId}`);
      toast.success('Publicación eliminada');
      navigate('/app/feed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-cnib-blue" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-10 text-center">
        <p className="text-slate-600">La publicación no existe o fue eliminada.</p>
        <Link to="/app/feed" className="mt-4 inline-block rounded-full bg-orange-500 px-6 py-2 text-sm font-bold text-white">
          Volver al feed
        </Link>
      </div>
    );
  }

  const canDelete = post.user_id === user?.user_id || user?.role === 'admin' || user?.role === 'superadmin';

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="post-detail">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-900"
          data-testid="post-detail-back"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </button>
        {canDelete && (
          <button
            type="button"
            onClick={handleDelete}
            className="flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-100"
            data-testid="post-detail-delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Eliminar
          </button>
        )}
      </div>

      <PostCard post={post} onLike={handleLike} showComments={false} />

      <section className="rounded-2xl border border-slate-200 bg-white" data-testid="post-detail-comments">
        <h2 className="border-b border-slate-100 px-5 py-3 font-heading text-base font-extrabold text-slate-900">
          Comentarios ({comments.length})
        </h2>

        <form onSubmit={handleAddComment} className="flex items-start gap-3 border-b border-slate-100 p-4">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
          ) : (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cnib-blue text-sm font-bold text-white">
              {user?.name?.charAt(0) || 'S'}
            </span>
          )}
          <div className="flex-1 flex items-end gap-2 rounded-full border border-slate-200 bg-slate-50 pl-4 pr-1 py-1">
            <input
              ref={inputRef}
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Escribe un comentario…"
              maxLength={1500}
              className="flex-1 bg-transparent py-1.5 text-sm outline-none placeholder:text-slate-400"
              data-testid="post-detail-comment-input"
            />
            <button
              type="submit"
              disabled={posting || !text.trim()}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500 text-white transition-colors hover:bg-orange-600 disabled:opacity-50"
              data-testid="post-detail-comment-submit"
            >
              {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            </button>
          </div>
        </form>

        {comments.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-slate-500" data-testid="post-detail-comments-empty">
            Sé el primero en comentar.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {comments.map((c) => (
              <li key={c.comment_id} className="flex gap-3 px-5 py-3" data-testid={`post-detail-comment-${c.comment_id}`}>
                {c.author_picture ? (
                  <img src={c.author_picture} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
                ) : (
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-sm font-bold text-slate-700">
                    {c.author_name?.charAt(0) || 'S'}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="rounded-2xl bg-slate-50 px-3 py-2">
                    <p className="text-xs font-bold text-slate-900">{c.author_name}</p>
                    <p className="whitespace-pre-line text-sm text-slate-700">{c.text}</p>
                  </div>
                  <p className="mt-1 pl-3 text-[11px] text-slate-400">hace {timeAgo(c.created_at)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
